/**
 * Squad Planning views-toggle glyphs, drawn from Figma V1.6 node
 * 40000926-34349.
 *
 * **Documented exception to the lucide-only iconography rule (RFC §3),
 * approved by Raniel on 2026-07-31 under SE-6503.** These three glyphs have
 * no faithful lucide equivalent: matched against the full 1,378-icon lucide
 * set (IoU over normalised rasters), the closest candidates scored 0.53
 * (Columns), 0.59 (Table) and 0.04 (Actions overview) — the previously
 * shipped `ArrowLeftRight` ranked 1093rd. The node draws two detached panels
 * where `Columns2` draws one split box, a header band over three columns
 * where `Table` draws a 2x3 grid, and a double-headed arrow crossing a
 * vertical centre line where `ArrowLeftRight` draws two stacked arrows.
 *
 * Geometry is the node's own vectors mapped onto the 24-unit grid, so the
 * glyphs render 12px of ink at the `Icon` atom's `sm` (16px) frame — the
 * node's icon frame. The numbers are exact, not eyeballed: the Actions
 * vectors land on integers and the Table dividers on exact thirds. Do not
 * "tidy" them; they are measured against the design.
 *
 * Colour is deliberately left to `currentColor` so the `Icon` atom's content
 * tokens keep driving it in both themes.
 * @module components/molecules/icons/viewToggleIcons
 */

import React, { memo } from "react";
import Svg, { Path } from "react-native-svg";

/** Default size matching lucide icons. */
const DEFAULT_ICON_SIZE = 24;

/**
 * Default stroke on the 24-unit grid. The node draws these at a 16px frame
 * with a 1.3333px stroke; 1.3333 ÷ (16/24) = 2.
 */
const DEFAULT_STROKE_WIDTH = 2;

/** Props matching the lucide icon interface consumed by the `Icon` atom. */
export interface ViewToggleIconProps {
  /** Icon size in pixels */
  readonly size?: number;
  /** Stroke colour */
  readonly color?: string;
  /** Stroke colour (alias, used by GlueStack Icon) */
  readonly stroke?: string;
  /** Stroke width on the 24-unit grid */
  readonly strokeWidth?: number;
}

/**
 * Two detached panels separated by a gap — the node's Columns glyph.
 * @param props - Icon props
 * @param props.size - Icon size in pixels (default 24)
 * @param props.color - Stroke colour
 * @param props.stroke - Stroke colour alias
 * @param props.strokeWidth - Stroke width (default 2)
 */
const ColumnsIcon = ({
  size = DEFAULT_ICON_SIZE,
  color,
  stroke,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  ...rest
}: ViewToggleIconProps) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <Path
      d="M4.125 4.125V19.875H10.25V4.125H4.125ZM13.75 4.125V19.875H19.875V4.125H13.75Z"
      stroke={color ?? stroke ?? "currentColor"}
      strokeWidth={strokeWidth}
    />
  </Svg>
);

ColumnsIcon.displayName = "ColumnsIcon";

/**
 * A header band over three equal columns — the node's Table glyph. The
 * dividers sit on exact thirds of the 4.125..19.875 span.
 * @param props - Icon props
 * @param props.size - Icon size in pixels (default 24)
 * @param props.color - Stroke colour
 * @param props.stroke - Stroke colour alias
 * @param props.strokeWidth - Stroke width (default 2)
 */
const TableIcon = ({
  size = DEFAULT_ICON_SIZE,
  color,
  stroke,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  ...rest
}: ViewToggleIconProps) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <Path
      d="M4.125 4.125H19.875V19.875H4.125V4.125ZM4.125 9.375H19.875M9.375 9.375V19.875M14.625 9.375V19.875"
      stroke={color ?? stroke ?? "currentColor"}
      strokeWidth={strokeWidth}
    />
  </Svg>
);

TableIcon.displayName = "TableIcon";

/**
 * Arrows pointing outward from a solid vertical centre line — the node's
 * Actions overview glyph, keeping its square caps.
 *
 * The art is inset so the glyph measures 12px of ink at the 16px frame, the
 * same as its two neighbours. Drawing it edge-to-edge on the 24-grid renders
 * 13.3px, because square caps add a stroke-width beyond the arrow tips,
 * which left this one visibly larger than the others.
 * @param props - Icon props
 * @param props.size - Icon size in pixels (default 24)
 * @param props.color - Stroke colour
 * @param props.stroke - Stroke colour alias
 * @param props.strokeWidth - Stroke width (default 2)
 */
const ActionsOverviewIcon = ({
  size = DEFAULT_ICON_SIZE,
  color,
  stroke,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  ...rest
}: ViewToggleIconProps) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <Path
      d="M6.6 9.3L3.9 12L6.6 14.7M12 4.8V19.2M19 12H5M17.4 9.3L20.1 12L17.4 14.7"
      stroke={color ?? stroke ?? "currentColor"}
      strokeWidth={strokeWidth}
      strokeLinecap="square"
    />
  </Svg>
);

ActionsOverviewIcon.displayName = "ActionsOverviewIcon";

const MemoColumnsIcon = memo(ColumnsIcon);
const MemoTableIcon = memo(TableIcon);
const MemoActionsOverviewIcon = memo(ActionsOverviewIcon);

export {
  MemoColumnsIcon as ColumnsIcon,
  MemoTableIcon as TableIcon,
  MemoActionsOverviewIcon as ActionsOverviewIcon,
};
